import {
  Game,
  GameState,
  PlayerPublic,
} from "@aura-frontend/omnibus-az-client";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import tagBattingPracticeBg from "@/assets/tag-batting-practice-bg.webp";
import tagBullpenBg from "@/assets/tag-bullpen-bg.webp";
import tagGameBg from "@/assets/tag-game-bg.webp";

import { ActiveGameModal } from "../components/modals/ActiveGameModal";
import { useGetActiveGame } from "../hooks/useGetActiveGame";
import { BullpenIcon, FieldIcon, HomePlateIcon } from "./icons";
import { ModeCard } from "./ModeCard";

type SessionMode = "game" | "bullpen" | "batting-practice";

/**
 * Welcome page - Mode selection for tagging session
 *
 * Allows user to select between Game, Batting Practice, or Bullpen modes.
 * Checks for active games and prompts user to resume or end them.
 * Mode views handle their own setup (modals, mutations, Redux dispatches).
 */
const BG_IMAGES = [tagGameBg, tagBattingPracticeBg, tagBullpenBg];

const Welcome = () => {
  const navigate = useNavigate();
  const { fetchActiveGame } = useGetActiveGame();
  const [bgIndex, setBgIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setBgIndex((prev) => (prev + 1) % BG_IMAGES.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const [loadingMode, setLoadingMode] = useState<string | null>(null);
  const [selectedMode, setSelectedMode] = useState<string | null>(null);
  const [activeGameModalOpened, setActiveGameModalOpened] = useState(false);
  const [activeGamePayload, setActiveGamePayload] = useState<{
    gameState: GameState;
    game: Game;
    awayTeamPlayers: PlayerPublic[];
    homeTeamPlayers: PlayerPublic[];
  }>();

  const handleModeSelect = async (mode: SessionMode) => {
    setLoadingMode(mode);
    const activeGamePayload = await fetchActiveGame();
    setLoadingMode(null);

    if (!activeGamePayload) {
      // No active game — navigate directly, mode view handles setup
      navigate(mode, { state: { showSetup: true } });
      return;
    }

    // Active game found — show modal
    setSelectedMode(mode);
    setActiveGamePayload({
      gameState: activeGamePayload.gameState as GameState,
      game: activeGamePayload.game as Game,
      awayTeamPlayers: activeGamePayload.awayTeamPlayers,
      homeTeamPlayers: activeGamePayload.homeTeamPlayers,
    });
    setActiveGameModalOpened(true);
  };

  return (
    <>
      <div className="relative h-full w-full">
        {/* Background images with fade transition */}
        {BG_IMAGES.map((src, i) => (
          <div
            key={src}
            className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000"
            style={{
              backgroundImage: `url(${src})`,
              opacity: bgIndex === i ? 1 : 0,
            }}
          />
        ))}

        {/* Left panel with mode cards */}
        <div className="absolute left-0 top-0 h-full w-full max-w-xs overflow-y-auto py-6 pl-16">
          <div className="flex flex-col gap-3">
            <ModeCard
              label="Game"
              icon={<FieldIcon className="h-6 w-6" />}
              onClick={() => handleModeSelect("game")}
              isLoading={loadingMode === "game"}
            />

            <ModeCard
              label="Batting Practice"
              icon={<HomePlateIcon className="h-5 w-5" />}
              onClick={() => handleModeSelect("batting-practice")}
              isLoading={loadingMode === "batting-practice"}
            />

            <ModeCard
              label="Bullpen"
              icon={<BullpenIcon className="h-4 w-4" />}
              onClick={() => handleModeSelect("bullpen")}
              isLoading={loadingMode === "bullpen"}
            />
          </div>
        </div>
      </div>

      {/* Active Game Modal */}
      {activeGamePayload && (
        <ActiveGameModal
          opened={activeGameModalOpened}
          onClose={() => setActiveGameModalOpened(false)}
          gameState={activeGamePayload.gameState}
          game={activeGamePayload.game}
          awayTeamPlayers={activeGamePayload.awayTeamPlayers}
          homeTeamPlayers={activeGamePayload.homeTeamPlayers}
          selectedMode={selectedMode ?? undefined}
        />
      )}
    </>
  );
};

export default Welcome;
